import { useEffect, useState } from "react";
import { RotateCcw, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, fmtDate } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../components/ui/dialog";

function EraseDialog({ book, onClose, onDone }) {
  const [t, setT] = useState("");
  const erase = async () => { try { const { data } = await api.post(`/trash/${book.id}/erase`, { confirm_title: t }); toast.success(`Erased permanently (${data.records} records, ${data.files} files)`); onDone(); } catch (e) { toast.error(errMsg(e)); } };
  return (
    <Dialog open={!!book} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-slate-950 border-slate-800" data-testid="erase-dialog">
        <DialogHeader><DialogTitle className="font-display">Erase permanently?</DialogTitle>
          <DialogDescription className="text-slate-400">This removes the book, every version, upload, audio track, cover, export and job record. It cannot be undone. Type <span className="text-white">{book?.title}</span> to confirm.</DialogDescription></DialogHeader>
        <Input value={t} onChange={(e) => setT(e.target.value)} placeholder="Book title" data-testid="erase-confirm-input" />
        <div className="flex justify-end gap-2"><Button variant="outline" className="border-slate-700" onClick={onClose} data-testid="erase-cancel">Cancel</Button>
          <Button onClick={erase} disabled={t.trim() !== book?.title.trim()} className="bg-red-700 hover:bg-red-600" data-testid="erase-confirm-button">Erase forever</Button></div>
      </DialogContent>
    </Dialog>
  );
}

export default function TrashPage() {
  const [d, setD] = useState(null);
  const [erase, setErase] = useState(null);
  const [limit, setLimit] = useState(50);
  const load = () => api.get(`/trash?limit=${limit}`).then((r) => setD(r.data));
  useEffect(() => { load(); }, [limit]); // eslint-disable-line
  const restore = async (b) => { try { const { data } = await api.post(`/trash/${b.id}/restore`); data.note ? toast.warning(`${b.title} restored. ${data.note}`) : toast.success(`${b.title} restored to My Books`); load(); } catch (e) { toast.error(errMsg(e)); } };
  const [emptying, setEmptying] = useState(false);
  const empty = async () => {
    if (!window.confirm(`Permanently erase all ${d.total} book${d.total === 1 ? "" : "s"} in Trash? This can't be undone.`)) return;
    setEmptying(true);
    try { const { data } = await api.post("/trash/empty"); toast.success(`Trash emptied — ${data.books} book${data.books === 1 ? "" : "s"} erased permanently`); load(); }
    catch (e) { toast.error(errMsg(e)); } finally { setEmptying(false); }
  };
  if (!d) return null;
  return (
    <div className="max-w-4xl" data-testid="trash-page">
      <p className="eyebrow mb-2">Recovery</p>
      <div className="flex flex-wrap items-end justify-between gap-3">
        <h1 className="font-display text-4xl sm:text-5xl">Trash</h1>
        {d.total > 0 && <Button variant="outline" className="border-red-800 text-red-300 hover:bg-red-950/40" onClick={empty} disabled={emptying} data-testid="trash-empty-all-button"><Trash2 size={14} className="mr-1" />{emptying ? "Emptying…" : "Empty Trash"}</Button>}
      </div>
      <p className="text-sm text-slate-400 mt-3 mb-8" data-testid="trash-policy">{d.days ? `Books stay here for ${d.days} days, then they're permanently erased. Restore one any time before that.` : "Automatic erasing is off — books stay here until you erase them."}</p>
      {!d.books.length && <p className="panel p-6 text-sm text-slate-500" data-testid="trash-empty">Trash is empty.</p>}
      <div className="panel divide-y divide-slate-800">
        {d.books.map((b) => (
          <div key={b.id} className="p-4 flex flex-wrap items-center justify-between gap-3" data-testid={`trash-row-${b.id}`}>
            <div><p className="text-sm text-slate-200">{b.title}</p><p className="text-xs text-slate-500">Deleted {fmtDate(b.deleted_at)}{b.erase_on && <span className="text-amber-500"> · erased on {fmtDate(b.erase_on)}</span>}</p></div>
            <div className="flex gap-2">
              <Button size="sm" variant="outline" className="border-slate-700" onClick={() => restore(b)} data-testid={`trash-restore-${b.id}`}><RotateCcw size={13} className="mr-1" />Restore</Button>
              <Button size="sm" variant="outline" className="border-red-800 text-red-300" onClick={() => setErase(b)} data-testid={`trash-erase-${b.id}`}><Trash2 size={13} className="mr-1" />Erase now</Button>
            </div>
          </div>
        ))}
      </div>
      {d.total > d.books.length && <Button variant="outline" className="border-slate-700 mt-4" onClick={() => setLimit(Math.min(limit + 50, 200))} disabled={limit >= 200} data-testid="trash-show-more">Show more ({d.total - d.books.length} more)</Button>}
      <EraseDialog book={erase} onClose={() => setErase(null)} onDone={() => { setErase(null); load(); }} />
    </div>
  );
}
